/**
 * Static style map for CtaButton color variants and size padding.
 *
 * Uses Cloudscape Core's Style API to apply per-instance theming.
 * Each CSS variable includes a hardcoded fallback so the button renders
 * correctly even before poly-marketing-theme-v1.css is parsed (prevents FOUC).
 *
 * Cloudscape applies background, border color, text color and shadow from this
 * map only through its own hashed class rules, so they are unreachable when the
 * pre-rendered markup and the loaded CSS come from different Cloudscape builds.
 * The resting background and border for each variant are therefore also declared
 * in CtaButton.scss on the stable `.poly-cta-button--{variant}` classes; this map
 * remains the source for hover, active and disabled states.
 *
 * Contrast (highContrast) fallbacks use the contrastDark values — the default
 * on light backgrounds. On dark backgrounds the CSS variables are reassigned
 * by the `.background-dark` context class in the theme stylesheet.
 */
import type { ButtonProps } from '@amzn/awsui-components-core/button';
import type { CtaButtonColorVariant, CtaButtonSize } from './CtaButton';

/* ── Shared disabled state ─────────────────────────────────── */

const DISABLED_BG = 'var(--poly-color-button-background-disabled, #D3D5D8)';
const DISABLED_TEXT = 'var(--poly-color-button-text-disabled, #5F6B7A)';
const DISABLED_BORDER = 'var(--poly-color-button-border-disabled, #D3D5D8)';
const TRANSPARENT = 'var(--poly-color-button-border-transparent, transparent)';
const TRANSPARENT_BG = 'transparent'; // tertiary has no background token; always transparent

/* ── Color variant styles ──────────────────────────────────── */

const COLOR_STYLES: Record<CtaButtonColorVariant, NonNullable<ButtonProps['style']>['root']> = {
  primary: {
    background: {
      default: 'var(--poly-color-button-background-primary-default, #FF6200)',
      hover: 'var(--poly-color-button-background-primary-hover, #F55600)',
      active: 'var(--poly-color-button-background-primary-active, #F55600)',
      disabled: DISABLED_BG,
    },
    borderColor: {
      default: TRANSPARENT,
      hover: TRANSPARENT,
      active: TRANSPARENT,
      disabled: TRANSPARENT,
    },
    color: {
      default: 'var(--poly-color-button-text-primary-default, #161D26)',
      hover: 'var(--poly-color-button-text-primary-default, #161D26)',
      active: 'var(--poly-color-button-text-primary-default, #161D26)',
      disabled: DISABLED_TEXT,
    },
  },

  contrast: {
    background: {
      default: 'var(--poly-color-button-background-highContrast-default, #161D26)',
      hover: 'var(--poly-color-button-background-highContrast-hover, #232F3E)',
      active: 'var(--poly-color-button-background-highContrast-active, #000000)',
      disabled: DISABLED_BG,
    },
    borderColor: {
      default: TRANSPARENT,
      hover: TRANSPARENT,
      active: TRANSPARENT,
      disabled: TRANSPARENT,
    },
    color: {
      default: 'var(--poly-color-button-text-highContrast-default, #FFFFFF)',
      hover: 'var(--poly-color-button-text-highContrast-default, #FFFFFF)',
      active: 'var(--poly-color-button-text-highContrast-default, #FFFFFF)',
      disabled: DISABLED_TEXT,
    },
  },

  offWhite: {
    background: {
      default: 'var(--poly-color-button-background-offWhite-default, #F5F3EF)',
      hover: 'var(--poly-color-button-background-offWhite-hover, #E8E6E2)',
      active: 'var(--poly-color-button-background-offWhite-active, #DDD9D5)',
      disabled: DISABLED_BG,
    },
    borderColor: {
      default: TRANSPARENT,
      hover: TRANSPARENT,
      active: TRANSPARENT,
      disabled: TRANSPARENT,
    },
    color: {
      default: 'var(--poly-color-button-text-offWhite-default, #161D26)',
      hover: 'var(--poly-color-button-text-offWhite-default, #161D26)',
      active: 'var(--poly-color-button-text-offWhite-default, #161D26)',
      disabled: DISABLED_TEXT,
    },
  },

  secondary: {
    background: {
      default: 'var(--poly-color-button-background-lowContrast-default, rgba(22,29,38,0.01))',
      hover: 'var(--poly-color-button-background-lowContrast-hover, rgba(22,29,38,0.05))',
      active: 'var(--poly-color-button-background-lowContrast-active, rgba(22,29,38,0.1))',
      disabled: DISABLED_BG,
    },
    borderColor: {
      default: 'var(--poly-color-button-border-lowContrast-default, #161D26)',
      hover: 'var(--poly-color-button-border-lowContrast-default, #161D26)',
      active: 'var(--poly-color-button-border-lowContrast-default, #161D26)',
      disabled: DISABLED_BORDER,
    },
    color: {
      default: 'var(--poly-color-button-text-lowContrast-default, #161D26)',
      hover: 'var(--poly-color-button-text-lowContrast-default, #161D26)',
      active: 'var(--poly-color-button-text-lowContrast-default, #161D26)',
      disabled: DISABLED_TEXT,
    },
  },

  tertiary: {
    background: {
      default: TRANSPARENT_BG,
      hover: TRANSPARENT_BG,
      active: TRANSPARENT_BG,
      disabled: TRANSPARENT_BG,
    },
    borderColor: {
      default: TRANSPARENT,
      hover: TRANSPARENT,
      active: TRANSPARENT,
      disabled: TRANSPARENT,
    },
    color: {
      default: 'var(--poly-color-button-text-lowContrast-default, #161D26)',
      hover: 'var(--poly-color-button-text-lowContrast-default, #161D26)',
      active: 'var(--poly-color-button-text-lowContrast-default, #161D26)',
      disabled: DISABLED_TEXT,
    },
  },
};

/* ── Size padding ──────────────────────────────────────────── */

interface SizePadding {
  paddingBlock: string;
  paddingInline: string;
}

const SIZE_PADDING: Record<CtaButtonSize, SizePadding> = {
  default: {
    paddingBlock: 'var(--poly-size-button-paddingVertical, 20px)',
    paddingInline: 'var(--poly-size-button-paddingHorizontal, 36px)',
  },
  medium: {
    paddingBlock: 'var(--poly-size-button-paddingVertical-medium, 14px)',
    paddingInline: 'var(--poly-size-button-paddingHorizontal-medium, 20px)',
  },
  small: {
    paddingBlock: 'var(--poly-size-button-paddingVertical-small, 12px)',
    paddingInline: 'var(--poly-size-button-paddingHorizontal-small, 14px)',
  },
  nav: {
    paddingBlock: 'var(--poly-size-button-paddingVertical-nav, 0.8333em)',
    paddingInline: 'var(--poly-size-button-paddingHorizontal-nav, 1.555em)',
  },
  compact: {
    paddingBlock: 'var(--poly-size-button-paddingVertical-compact, 12px)',
    paddingInline: 'var(--poly-size-button-paddingHorizontal-compact, 14px)',
  },
};

const NAV_PADDING_WITH_ICON: SizePadding = {
  paddingBlock: SIZE_PADDING.nav.paddingBlock,
  paddingInline: 'var(--poly-size-button-paddingLeft-nav, 1.111em) var(--poly-size-button-paddingHorizontal-nav, 1.555em)',
};

const MEDIUM_PADDING_WITH_TRAILING_ICON: SizePadding = {
  paddingBlock: SIZE_PADDING.medium.paddingBlock,
  paddingInline:
    'var(--poly-size-button-paddingHorizontal-medium) var(--poly-size-button-paddingRight-trailingIcon-medium)',
};

const resolveSizePadding = (size: CtaButtonSize, hasIcon: boolean, hasTrailingIcon: boolean): SizePadding => {
  if (size === 'nav' && hasIcon) return NAV_PADDING_WITH_ICON;
  if (size === 'medium' && hasTrailingIcon) return MEDIUM_PADDING_WITH_TRAILING_ICON;
  return SIZE_PADDING[size];
};

/* ── Public helpers ────────────────────────────────────────── */

export interface GetButtonStyleOptions {
  colorVariant: CtaButtonColorVariant;
  size: CtaButtonSize;
  hasIcon: boolean;
  /** True when an icon sits after the label (e.g. the Watch video play icon). */
  hasTrailingIcon?: boolean;
}

/**
 * Inline styles applied to the wrapper div to prevent FOUC.
 *
 * Cloudscape's Style API sets `--awsui-*` CSS custom variables on the `<a>`
 * element, but those variables are inert until Cloudscape's external stylesheet
 * loads and consumes them. During the gap the browser applies `<a>` tag
 * defaults (blue text, underline). The inline `color` on the wrapper cascades
 * into the `<a>` via the bundled SCSS rule `a { color: inherit }`.
 * `text-decoration` is handled purely in SCSS (`a { text-decoration: none }`)
 * since it's not an inherited property and can't be set via the wrapper.
 */
interface FoucStyle {
  color: string;
}

// Hardcoded hex instead of Cloudscape/Poly design tokens because this is a FOUC
// fix: CSS custom properties (var(--poly-*)) aren't resolved until the stylesheet
// loads, which is exactly the gap we're bridging. Plain hex values are applied by
// the browser immediately as inline styles, before any external CSS is parsed.
const CTA_TEXT_COLORS = { dark: '#161D26', light: '#FFFFFF' } as const;

const FOUC_STYLES: Record<CtaButtonColorVariant, FoucStyle> = {
  primary: { color: CTA_TEXT_COLORS.dark },
  contrast: { color: CTA_TEXT_COLORS.light },
  offWhite: { color: CTA_TEXT_COLORS.dark },
  secondary: { color: CTA_TEXT_COLORS.dark },
  tertiary: { color: CTA_TEXT_COLORS.dark },
};

export const getFoucStyle = (colorVariant: CtaButtonColorVariant): FoucStyle =>
  FOUC_STYLES[colorVariant];

/** Build the Cloudscape `style` prop for a given variant + size. */
export const getButtonStyle = ({
  colorVariant,
  size,
  hasIcon,
  hasTrailingIcon = false,
}: GetButtonStyleOptions): ButtonProps['style'] => {
  const padding = resolveSizePadding(size, hasIcon, hasTrailingIcon);
  // Tertiary is a plain text link — padding is handled in SCSS (padding: 0)
  // so we must not apply inline padding that would override it.
  const resolvedPadding = colorVariant === 'tertiary' ? {} : padding;
  return {
    root: {
      ...resolvedPadding,
      ...COLOR_STYLES[colorVariant],
    },
  };
};
